<script setup lang="ts">
import VMenu from '@shared/component/VMenu/VMenu.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import VSkeleton from '@shared/component/VSkeleton/VSkeleton.vue';
import { type FontAwesomeIconProps } from '@fortawesome/vue-fontawesome';
import { useId } from '@tools/composables/id';
import VHeading from '@shared/typography/VHeading/VHeading.vue';
import VIconSquare from '@shared/component/VIconSquare/VIconSquare.vue';
import { type BasicColor } from '@tailwind-preset/colors';
import { getIconForState, type VSidebarItem } from './utils';

export interface HeaderMetadataItem {
  icon: FontAwesomeIconProps['icon'];
  label: string;
  href: string;
}

const props = withDefaults(
  defineProps<{
    items: VSidebarItem[];
    /**
     * Meta key voor het label (standaard 'label', gebruik 'title' voor Beheerbox).
     * TODO: verwijder deze prop zodra Beheerbox metadata is rechtgetrokken.
     */
    metaKey?: 'label' | 'title';
    headerIcon: FontAwesomeIconProps['icon'];
    headerIconColor?: BasicColor;
    headerTitle: string;
    headerSubtitle?: string;
    headerMetadata?: HeaderMetadataItem[];
    headerLoading?: boolean;
  }>(),
  {
    metaKey: 'label',
  },
);

const emit = defineEmits<{
  itemClick: [payload: { item: VSidebarItem; isActive: boolean }];
}>();

const currentRoute = useRoute();

const currentLabel = computed<string>(() => (currentRoute.meta[props.metaKey] as string) ?? '');
const currentIcon = computed<FontAwesomeIconProps['icon'] | undefined>(
  () => currentRoute.meta.icon as FontAwesomeIconProps['icon'] | undefined,
);

const showMobileMenu = ref(false);
const menuId = useId('side-menu');
const headerTitleId = useId('header-title');

const toggleButtonLabel = computed(() =>
  showMobileMenu.value ? `Sidebar menu sluiten` : `Sidebar menu openen`,
);

const toggleMobileMenu = () => {
  showMobileMenu.value = !showMobileMenu.value;
};

watch(currentRoute, () => {
  showMobileMenu.value = false;
});
</script>

<template>
  <nav
    :aria-labelledby="props.headerTitle ? headerTitleId : undefined"
    :class="[
      'sticky top-(--header-height) z-subheader h-(--subheader-height) w-full border-b border-grey-200 bg-grey-100',
      'xl:h-[calc(100dvh-var(--header-height,0px))] xl:max-w-64',
      '3xl:max-w-(--menu-container-width)',
    ]"
  >
    <button
      type="button"
      :aria-label="toggleButtonLabel"
      :aria-controls="menuId"
      :aria-expanded="showMobileMenu"
      :class="['w-full py-2', 'xl:hidden']"
      @click="toggleMobileMenu"
    >
      <div class="mx-auto max-w-full px-4">
        <div class="flex w-full items-center gap-2 py-2 text-sm font-bold">
          <FontAwesomeIcon v-if="currentIcon" :icon="currentIcon" />
          {{ currentLabel }}
          <div class="ml-auto shrink-0 px-2">
            <FontAwesomeIcon
              :icon="faChevronDown"
              class="transition-transform duration-300"
              :class="{ 'rotate-180': showMobileMenu }"
            />
          </div>
        </div>
      </div>
    </button>

    <div
      :id="menuId"
      :class="[
        'absolute mt-px w-full bg-grey-100 transition-all duration-300',
        'xl:static xl:mt-0 xl:h-full',
        showMobileMenu
          ? 'max-xl:h-[calc(100dvh-var(--header-height,0px)-var(--subheader-height,0px))]'
          : 'max-xl:h-0',
      ]"
    >
      <div
        :class="[
          'flex max-h-full min-h-0 flex-col divide-y divide-solid divide-grey-200',
          'xl:h-full',
          { 'max-xl:hidden': !showMobileMenu },
        ]"
      >
        <div
          :class="[
            'flex flex-wrap items-center gap-8 p-6 text-grey-900',
            'xl:flex-col xl:flex-nowrap xl:items-start',
          ]"
        >
          <div v-if="props.headerLoading" class="flex w-full gap-2">
            <VSkeleton class="!w-8 shrink-0 *:!h-8 *:!rounded-lg" />
            <div class="flex flex-1 flex-col gap-2">
              <VSkeleton :count="2" />
              <VSkeleton class="max-w-[60%]" />
            </div>
          </div>
          <template v-else>
            <div class="flex w-full gap-2">
              <header class="flex w-full items-center gap-2">
                <VIconSquare
                  class="shrink-0 self-start"
                  :icon="props.headerIcon"
                  :color="props.headerIconColor"
                />
                <div class="flex flex-col gap-2">
                  <VHeading
                    :id="headerTitleId"
                    :level="2"
                    appearance="sm"
                    class="flex gap-2 self-center leading-tight overflow-wrap-anywhere"
                  >
                    {{ props.headerTitle }}
                  </VHeading>
                  <p v-if="props.headerSubtitle" class="text-sm text-grey-500">
                    {{ props.headerSubtitle }}
                  </p>
                </div>
              </header>
            </div>
            <ul
              v-if="props.headerMetadata || $slots.headerContent"
              :class="['flex flex-wrap gap-4', 'xl:w-full xl:flex-col']"
            >
              <li
                v-for="(metadataItem, index) in props.headerMetadata"
                :key="index"
                class="flex items-center gap-ch text-sm overflow-wrap-anywhere text-grey-500"
              >
                <FontAwesomeIcon :icon="metadataItem.icon" />
                <a class="text-grey-600" :href="metadataItem.href">
                  {{ metadataItem.label }}
                </a>
              </li>
              <slot name="headerContent"></slot>
            </ul>
          </template>
        </div>
        <div class="min-h-0 grow scrollbar-thin overflow-auto p-2">
          <VMenu class="w-full" theme="secondary" :model="props.items">
            <template #item="{ item, styles: { textColor, activeBgColor, hoverColor } }">
              <RouterLink
                v-if="item.route"
                v-slot="{ href, navigate, isActive }"
                :to="item.route"
                custom
              >
                <slot
                  v-if="$slots.item"
                  name="item"
                  :item="item"
                  :styles="{ textColor, activeBgColor, hoverColor }"
                  :href="item.target === '_top' ? item.route : href"
                  :navigate="navigate"
                  :is-active="isActive"
                ></slot>
                <a
                  v-else
                  :href="item.target === '_top' ? item.route : href"
                  @click="
                    (event) => {
                      emit('itemClick', { item, isActive });
                      return item.target === '_top' ? event.stopPropagation() : navigate(event);
                    }
                  "
                  :target="item.target"
                  class="flex h-10 items-center gap-2 rounded p-2 text-sm overflow-wrap-anywhere"
                  :class="[
                    'hover:no-underline',
                    textColor,
                    hoverColor,
                    { [activeBgColor]: isActive, 'font-bold': isActive },
                  ]"
                >
                  <FontAwesomeIcon v-if="item.icon" :icon="getIconForState(item.icon, isActive)" />
                  {{ item.label }}
                  <slot
                    name="countBadge"
                    :item="item"
                    :indicator="item.indicator"
                    :is-active="isActive"
                  ></slot>
                </a>
              </RouterLink>
            </template>
          </VMenu>
        </div>
        <div v-if="$slots.actions" class="flex flex-col gap-2 p-4 *:justify-start">
          <slot name="actions"></slot>
        </div>
      </div>
    </div>
  </nav>
</template>
